import * as Sentry from '@sentry/vue'
import { applyStateFromContract } from 'src/composables/useChangeRateData'
import { useDsQueryParams } from 'src/composables/useDsQueryParams'
import { selectionProps } from 'src/helpers/searchConfiguration'
import { useOrderStore } from 'src/stores/orders'

let lastQuery = null
const resilientImport = (webpackPromise, pushRoute = null) => {
  return webpackPromise
    .then(module => {
      lastQuery = null
      return module
    })
    .catch(e => {
      Sentry.captureMessage('Automatically resolving chunk load problem', scope => {
        scope.setExtra('originalError', e.stack)
        scope.setExtra('pushRoute', pushRoute)
      })
      if (pushRoute !== null && pushRoute !== undefined) {
        console.warn(`navigating to ${pushRoute}`)
        let href = pushRoute
        if (lastQuery) {
          href += `?${new URLSearchParams(Object.entries(lastQuery))?.toString()}`
        }
        window.location.href = href
      } else {
        window.location.reload()
      }
    })
}

const importSelectionPage = (pushRoute) => resilientImport(import(/* webpackChunkName: "selection" */'src/pages/Selection.vue'), pushRoute)
const importOrderPage = (pushRoute) => resilientImport(import(/* webpackChunkName: "order" */'src/pages/Order.vue'), pushRoute)

export default (store) => {
  const routes = [
    {
      path: '/',
      component: () => resilientImport(import(/* webpackChunkName: "mainLayout" */'layouts/MainLayout.vue'), ''),
      beforeEnter: (to) => {
        if (to.query.simulationMode === 'active') {
          useOrderStore().enterSimulationMode()
        }
        lastQuery = to.query
        return true
      },
      children: [
        {
          path: '',
          name: 'selection',
          component: () => importSelectionPage(''),
          props: route => {
            const { effectiveProps } = useDsQueryParams({
              propsDeclaration: selectionProps,
              applicableProps: {
                energyType: null, // no default energy type
                productId: null,
                tag: null
              },
              dataProvider: route.query,
              propTransform: {
                tag: v => typeof v === 'string' ? [v] : v
              }
            })
            return {
              queryDataProvider: route.query,
              ...effectiveProps
            }
          },
          meta: {
            hideNavigationBar: true
          }
        },
        {
          path: '/energy-types',
          name: 'energyTypes',
          component: () => resilientImport(import(/* webpackChunkName: "energyTypes" */'src/pages/EnergyType.vue'), 'energy-types'),
          meta: {
            hideNavigationBar: true
          }
        },
        {
          path: '/order/existing-customer-ssp',
          name: 'login',
          component: () => importOrderPage('order'),
          beforeEnter: (_to, _from, next) => {
            useOrderStore().setAccessGranted()
            next('/order')
          }
        },
        {
          path: '/order',
          name: 'order',
          component: () => importOrderPage('order')
        },
        {
          path: '/change-rate',
          name: 'changeRateSelection',
          component: () => importSelectionPage('change-rate'),
          props: (route) => {
            const dataParam = route.query.data
            const changeRateProps = applyStateFromContract(dataParam)

            return {
              fromContract: true,
              paramsResolved: changeRateProps != null,
              ...(changeRateProps ?? {})
            }
          }
        },
        {
          path: '/change-rate/order',
          name: 'changeRateInput',
          component: () => resilientImport(import(/* webpackChunkName: "changeRate" */'src/pages/ChangeRate.vue'), 'change-rate/order')
        },
        {
          path: '/change-rate/order/existing-customer',
          component: () => resilientImport(import(/* webpackChunkName: "changeRate" */'src/pages/ChangeRate.vue'), 'change-rate/order'),
          beforeEnter: (_to, _from, next) => {
            useOrderStore().setAccessGranted()
            next('/change-rate/order')
          }
        },
        {
          path: '/change-rate/verify',
          component: () => resilientImport(import(/* webpackChunkName: "changeRateVerification" */'src/pages/ChangeRateVerification.vue'), 'change-rate/verify'),
          meta: {
            hideNavigationBar: true
          }
        },
        {
          path: '/:catchAll(.*)*',
          component: () => resilientImport(import(/* webpackChunkName: "error" */'pages/error/OrderError.vue')),
          meta: {
            hideNavigationBar: true
          },
          props: {
            title: 'notFound.title',
            message: 'notFound.description',
            actionMessage: 'notFound.action',
            action: () => { window.location.href = '' }
          }
        }
      ]
    }
  ]

  return routes
}
